export type FeedItemRow = {
  id: number;
  post_id: string;
  shortcode: string | null;
  post_type: string | null;
  item_index: number | null;
  media_type: string | null;
  fecha_utc: string | null;
  descripcion: string | null;
  link: string | null;
  archivo: string | null;
  published: boolean;
};

/**
 * Las columnas de arriba, para pedirlas por su nombre en vez de con `*`.
 *
 * La tabla tiene seis mas que nadie lee aqui (created_at, updated_at,
 * instagram_media_id, instagram_permalink, instagram_synced_at y
 * manual_override). Medido contra produccion, pedirlas de mas costaba 0,35 s
 * por pagina en vez de 0,22 y 280 KB extra de JSON que el navegador tiene
 * que parsear antes de pintar la primera foto.
 *
 * Va pegado al tipo a proposito: si alguien le añade un campo y no lo añade
 * aqui, el test de contentModels lo canta.
 */
export const COLUMNAS_FEED_ITEM =
  "id,post_id,shortcode,post_type,item_index,media_type,fecha_utc,descripcion,link,archivo,published";

export type PlaylistRow = {
  id: string;
  title: string;
  description: string;
  spotify_url: string;
  published: boolean;
  created_at: string;
  sort_order?: number | null;
};

export type SpotifyItemType = "playlist" | "album" | "track" | "spotify";

/** Fila del archivo histórico (tabla `blogs` en Supabase). */
export type BlogArchiveRow = {
  post_num: number;
  created_at?: string | null;
  fecha_post?: string | null;
  source_archive_url?: string | null;
  fecha: string | null;
  titulo: string;
  link: string | null;
  descripcion: string | null;
  /** Solo en entradas cargadas desde la tabla `blogs` (blog retro). */
  labels?: string | null;
  num_imagenes: number;
  imagenes: string | null;
  published: boolean;
  /** Relleno al pasar un concierto de IG a artículo; evita que el sync lo vuelva a importar. */
  source_instagram_post_id?: string | null;
};

export type ArticleRow = BlogArchiveRow;

const MONTHS_ES = [
  "enero",
  "febrero",
  "marzo",
  "abril",
  "mayo",
  "junio",
  "julio",
  "agosto",
  "septiembre",
  "octubre",
  "noviembre",
  "diciembre",
];

export function getArticleDateParts(row: ArticleRow): { year: number; month: number; day?: number } | null {
  if (row.fecha_post) {
    const [yearRaw, monthRaw, dayRaw] = row.fecha_post.split("-");
    const year = Number(yearRaw);
    const month = Number(monthRaw);
    const day = Number(dayRaw);
    if (year && month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return { year, month, day };
    }
  }

  if (!row.created_at) return null;
  const parsed = new Date(row.created_at);
  if (Number.isNaN(parsed.getTime())) return null;
  return { year: parsed.getFullYear(), month: parsed.getMonth() + 1, day: parsed.getDate() };
}

export function getArticleDateLabel(row: ArticleRow): string {
  const dateParts = getArticleDateParts(row);
  if (dateParts) {
    if (dateParts.day) {
      const monthName = MONTHS_ES[dateParts.month - 1];
      return `${dateParts.day} ${monthName} ${dateParts.year}`;
    }
    if (row.created_at) {
      const parsed = new Date(row.created_at);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
      }
    }
    const monthName = MONTHS_ES[dateParts.month - 1];
    return `${monthName} ${dateParts.year}`;
  }

  if (row.created_at) {
    const parsed = new Date(row.created_at);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed.toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });
    }
  }

  return row.fecha || "Fecha no disponible";
}

export function getArticleSortTime(row: ArticleRow): number {
  if (row.fecha_post) {
    const t = new Date(`${row.fecha_post}T00:00:00`).getTime();
    if (!Number.isNaN(t)) return t;
  }
  const raw = row.link ?? row.source_archive_url ?? "";
  const m = raw.match(/\/(\d{4})\/(\d{1,2})(?:\/(\d{1,2}))?\//);
  if (m) {
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = m[3] ? Number(m[3]) : 1;
    if (year && month >= 1 && month <= 12) return new Date(year, month - 1, day).getTime();
  }
  if (row.created_at) {
    const t = new Date(row.created_at).getTime();
    if (!Number.isNaN(t)) return t;
  }
  return row.post_num;
}

export function articleExcerpt(raw: string | null | undefined): string {
  const value = (raw ?? "").toString();
  if (!value.trim()) return "";
  // Normaliza imports legacy con "\\n" literal.
  const normalized = value.replace(/\\n/g, "\n");
  // Convierte links Markdown a texto visible.
  const withoutMdLinks = normalized.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1");
  // En resúmenes queremos una sola línea (sin saltos enormes).
  return withoutMdLinks.replace(/\s*\r?\n\s*/g, " ").replace(/\s{2,}/g, " ").trim();
}

export function articleExcerptWithSingleBreak(raw: string | null | undefined): string {
  const value = (raw ?? "").toString();
  if (!value.trim()) return "";
  const normalized = value.replace(/\\n/g, "\n");
  const withoutMdLinks = normalized.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, "$1");
  // Colapsa múltiples saltos y preserva como máximo 1 "\n".
  const collapsed = withoutMdLinks
    .replace(/\r\n/g, "\n")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/\n{2,}/g, "\n")
    .trim();
  const firstBreak = collapsed.indexOf("\n");
  if (firstBreak === -1) return collapsed.replace(/\s{2,}/g, " ");
  const head = collapsed.slice(0, firstBreak).replace(/\s{2,}/g, " ").trim();
  const tail = collapsed.slice(firstBreak + 1).replace(/\s+/g, " ").trim();
  return `${head}\n${tail}`.trim();
}

export function parseBlogImages(raw: string | null): string[] {
  if (!raw) return [];
  const normalize = (value: string): string | null => {
    const v = value.trim().replace(/\\/g, "/");
    if (!v) return null;
    if (v.startsWith("data:")) return v;

    const viaWeserv = (absoluteUrl: string): string => {
      // Many legacy blogs/images are http-only. Browsers block mixed content,
      // so we proxy through a https image CDN.
      try {
        const u = new URL(absoluteUrl);
        const upstream = `${u.host}${u.pathname}${u.search}`;
        return `https://images.weserv.nl/?url=${encodeURIComponent(upstream)}`;
      } catch {
        // If URL parsing fails, keep original.
        return absoluteUrl;
      }
    };

    const viaImageProxyIfNeeded = (absoluteUrl: string): string => {
      try {
        const u = new URL(absoluteUrl);
        // Instagram CDN blocks hotlinking (403) from third-party sites.
        // Route through an image proxy so browsers can load it reliably.
        if (u.hostname.endsWith("cdninstagram.com") || u.hostname.endsWith("fbcdn.net")) {
          const upstream = `${u.host}${u.pathname}${u.search}`;
          return `https://images.weserv.nl/?url=${encodeURIComponent(upstream)}`;
        }
      } catch {
        // ignore
      }
      return absoluteUrl;
    };

    // Fix mixed content (http) and protocol-relative URLs.
    if (v.startsWith("http://")) return viaWeserv(v);
    if (v.startsWith("//")) return viaImageProxyIfNeeded(`https:${v}`);

    // Absolute https already OK.
    if (v.startsWith("https://")) return viaImageProxyIfNeeded(v);

    // If stored as site-relative or relative path, assume it lives on popistant.es.
    // This matches legacy WordPress-style image paths.
    if (v.startsWith("/")) return viaImageProxyIfNeeded(`https://popistant.es${v}`);
    return viaImageProxyIfNeeded(`https://popistant.es/${v}`);
  };

  const trimmed = raw.trim();

  const dedupePreserveOrder = (items: Array<string | null>): string[] => {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const item of items) {
      const v = item ?? "";
      if (!v) continue;
      if (seen.has(v)) continue;
      seen.add(v);
      out.push(v);
    }
    return out;
  };

  // 1) JSON array (common when stored as textified arrays)
  if (trimmed.startsWith("[")) {
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return dedupePreserveOrder(
          parsed
            .filter((v): v is string => typeof v === "string")
            .map(normalize)
            .filter((v): v is string => Boolean(v))
        );
      }
    } catch {
      // fall through
    }
  }

  // 1.5) Plain delimited list (our own storage URLs, etc.) — skip regex extraction to avoid duplicates.
  if (/[;\n\r,|]/.test(trimmed) && !trimmed.includes("<") && !trimmed.includes("src=")) {
    const splitParts = trimmed.split(/[;\n\r,|]+/g);
    return dedupePreserveOrder(
      splitParts
        .map(normalize)
        .filter((v): v is string => Boolean(v))
    );
  }

  // 2) Extract from HTML/Markdown: src="..." or plain URLs
  const extracted: string[] = [];
  const srcRegex = /src\s*=\s*["']([^"']+)["']/gi;
  let m: RegExpExecArray | null;
  while ((m = srcRegex.exec(trimmed))) extracted.push(m[1]);

  const urlRegex = /(https?:\/\/[^\s<>"')]+|\/\/[^\s<>"')]+|\/wp-content\/[^\s<>"')]+|wp-content\/[^\s<>"')]+)/gi;
  while ((m = urlRegex.exec(trimmed))) extracted.push(m[1]);

  // 3) Split by common separators
  const splitParts = trimmed.split(/[;\n\r,|]+/g);

  return dedupePreserveOrder(
    [...extracted, ...splitParts]
      .map(normalize)
      .filter((v): v is string => Boolean(v))
  );
}

export function isVideoUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  const clean = url.split("?")[0].toLowerCase();
  return [".mp4", ".webm", ".ogg", ".mov", ".m4v"].some((ext) => clean.endsWith(ext));
}

/** Une textos de distintas filas del mismo post (p. ej. una media sin pie y otra con pie de foto). */
export function mergeFeedDescriptions(a: string | null | undefined, b: string | null | undefined): string {
  const ta = (a ?? "").trim();
  const tb = (b ?? "").trim();
  if (!tb) return ta;
  if (!ta) return tb;
  return tb.length >= ta.length ? tb : ta;
}

export function buildFeedTitle(item: FeedItemRow): string {
  const text = item.descripcion?.trim();
  if (text && text.length > 0) return text.slice(0, 60) + (text.length > 60 ? "..." : "");
  if (item.shortcode) return `Post ${item.shortcode}`;
  return "Concierto";
}

export function getSpotifyItemType(url: string | null): SpotifyItemType {
  if (!url) return "spotify";
  const value = url.toLowerCase();
  if (value.includes("/playlist/") || value.startsWith("spotify:playlist:")) return "playlist";
  if (value.includes("/album/") || value.startsWith("spotify:album:")) return "album";
  if (value.includes("/track/") || value.startsWith("spotify:track:")) return "track";
  return "spotify";
}

export function getSpotifyItemLabel(url: string | null): string {
  const type = getSpotifyItemType(url);
  if (type === "playlist") return "Playlists";
  if (type === "album") return "Album";
  if (type === "track") return "Cancion";
  return "Spotify";
}
